import { useQuery } from '@apollo/client'
import { useCallback, useMemo } from 'react'
import { ISSUE_FIELD_CONFIGS_QUERY } from './queries'
import type {
  IssueFieldConfig,
  IssueFieldConfigsQuery,
  IssueFormFieldValue,
  IssueFormMode,
} from './types'

export interface UseIssueFieldConfig {
  configs: IssueFieldConfig[]
  loading: boolean
  /**
   * Changes whenever the resolved configuration changes. Views re-run
   * `validateForm()` on it so requirements that arrive after first paint are
   * reflected in `formValid`.
   */
  version: string
  isVisible: (field: IssueFormFieldValue, mode: IssueFormMode) => boolean
  isRequired: (field: IssueFormFieldValue, mode: IssueFormMode) => boolean
  labelFor: (field: IssueFormFieldValue, fallback: string) => string
}

/** The company's issue form configuration. */
export function useIssueFieldConfig(): UseIssueFieldConfig {
  const { data, loading } = useQuery<IssueFieldConfigsQuery>(
    ISSUE_FIELD_CONFIGS_QUERY,
    { fetchPolicy: 'cache-first' }
  )

  const configs = useMemo(() => data?.issueFieldConfigs ?? [], [data])

  const byField = useMemo(() => {
    const map = new Map<IssueFormFieldValue, IssueFieldConfig>()
    for (const config of configs) map.set(config.field, config)
    return map
  }, [configs])

  const isVisible = useCallback(
    (field: IssueFormFieldValue, mode: IssueFormMode) => {
      const config = byField.get(field)
      // Unknown field (config not loaded yet, or one the backend does not
      // consider configurable): show it. Hiding on a miss would make the form
      // flicker empty on every cold load.
      if (!config) return true
      return mode === 'create' ? config.visibleOnCreate : config.visibleOnEdit
    },
    [byField]
  )

  const isRequired = useCallback(
    (field: IssueFormFieldValue, mode: IssueFormMode) => {
      const config = byField.get(field)
      if (!config) return false
      return mode === 'create' ? config.requiredOnCreate : config.requiredOnEdit
    },
    [byField]
  )

  const labelFor = useCallback(
    (field: IssueFormFieldValue, fallback: string) =>
      byField.get(field)?.labelOverride || fallback,
    [byField]
  )

  const version = useMemo(
    () =>
      configs
        .map(
          config =>
            `${
              config.field
            }:${+config.visibleOnCreate}${+config.requiredOnCreate}` +
            `${+config.visibleOnEdit}${+config.requiredOnEdit}`
        )
        .join('|'),
    [configs]
  )

  return { configs, loading, version, isVisible, isRequired, labelFor }
}
